<!----
    Author: hjlee
    Description:
        파일 업로더
---->
<template>
  <div class="file-uploader">
    <b-input-group>
      <b-form-file
        ref="file-input"
        v-model="file"
        name="file"
        class="mb-2"
        :accept="accept"
        :placeholder="placeholder?placeholder:$t('file_uploader.text.no_file_chosen')"
        :browse-text="browseText?browseText:$t('file_uploader.button.browse')"
      />
      <b-input-group-append>
        <b-button class="reset-file-button" :disabled="!file" @click="clearFile">
          {{ $t('file_uploader.button.deselect_file') }}
        </b-button>
      </b-input-group-append>
    </b-input-group>
    <b-alert :show="status !== 'ready'" class="status-area" :variant="(status === 'success')?'success':((status === 'fail')?'danger':'warning')">
      <span class="status-text">{{ $t('file_uploader.status.' + status) }}</span>
      <span v-if="message" class="message-text"> : {{ message }}</span>
    </b-alert>
    <b-modal
      id="file-uploading-progress-modal"
      ref="file-uploading-progress-modal"
      :title="$t('file_uploader.status.' + status)"
      :lazy="true"
      :hide-footer="true"
      size="sm"
      :no-close-on-esc="true"
      :no-close-on-backdrop="true"
      :hide-header-close="true"
    >
      <label>{{ $t('file_uploader.title.upload_progress') }}</label>
      <b-progress :max="100">
        <b-progress-bar :value="uploadRate?uploadRate:0" :label="(uploadRate?uploadRate:0) + '%'" />
      </b-progress>
      <div v-if="pollingUrl">
        <label>{{ $t('file_uploader.title.process_progress') }}</label>
        <b-progress :max="100">
          <b-progress-bar :value="processRate?processRate:0" :label="'(' + processCo + '/' + allCo + ')' + ' ' + (processRate?processRate:0) + '%'" />
        </b-progress>
      </div>
      <div class="status-area status-area-in-modal">
        <span class="status-text">{{ $t('file_uploader.status.' + status) }}</span>
        <span v-if="message" class="message-text"> : {{ message }}</span>
      </div>
      <div v-if="status === 'uploading' || cancelUrl" class="cancel-button-area">
        <b-button size="sm" @click="cancel">
          {{ $t('common.button.cancel') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
/*
  파일 업로드 상태
  'ready',
  'uploading',
  'processing',
  'success',
  'fail',
  'canceled'
*/
import axios from 'axios'
export default {
  props: {
    placeholder: {
      type: String,
      default: undefined
    },
    browseText: {
      type: String,
      default: undefined
    },
    accept: {
      type: String,
      default: undefined
    },
    value: {
      type: File,
      default: undefined
    },
    uploadUrl: {
      type: String,
      required: true
    },
    pollingUrl: {
      type: String,
      default: undefined
    },
    cancelUrl: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      status: 'ready',
      message: '',
      file: undefined,
      uploadRate: 0,
      processRate: 0,
      processCo: 0,
      allCo: 0
    }
  },
  watch: {
    value: function(newVal) {
      this.file = newVal
    },
    file: {
      handler(newVal, oldVal) {
        this.$emit('input', newVal)
        this.$emit('change', newVal)
      },
      immediate: true
    }
  },
  methods: {
    clearFile() {
      this.$refs['file-input'].reset()
    },
    upload() {
      const self = this
      const CancelToken = axios.CancelToken
      const source = (this.source = CancelToken.source())
      this.showModal('file-uploading-progress-modal')
      this.status = 'uploading'
      this.message = ''
      const formData = new FormData()
      formData.append('file', this.file)
      this.$axios
        .$post(this.uploadUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: function(progressEvent) {
            self.uploadRate =
              Math.round((10000 * progressEvent.loaded) / progressEvent.total) /
              100
          },
          cancelToken: source.token,
          no_loading: true
        })
        .then(response => {
          this.message = response.message
          if (response.resultCode === 'success') {
            this.$toast.info(this.$t(response.message, response.params))
            this.message = this.$t(response.message, response.params)
            if (this.pollingUrl) {
              this.startPolling()
              this.uploadPollingToken = response.data.token
              this.status = 'processing'
            } else {
              this.status = 'success'
              this.hideProgressModal()
              this.$emit('on-uploaded')
            }
          } else {
            console.error(this.$t(response.message, response.params))
            this.$toast.error(this.$t(response.message, response.params))
            this.message = this.$t(response.message, response.params)
            this.status = 'fail'
            this.hideProgressModal()
            this.$emit('on-failed')
          }
        })
        .catch(error => {
          this.hideProgressModal()
          this.$emit('on-failed')
          this.status = 'fail'
          if (error.response && error.response.data) {
            this.message = error.response.data.message
          } else {
            this.message = error
          }
        })
    },
    hideProgressModal() {
      this.hideModal('file-uploading-progress-modal')
      this.uploadRate = 0
      this.processRate = 0
    },
    cancel() {
      if (this.status === 'uploading') {
        this.source.cancel(
          this.$t('file_uploader.text.operation_canceled_by_the_user')
        )
        this.status = 'canceled'
        this.$emit('on-canceled')
        this.hideProgressModal()
      } else {
        this.$axios
          .$put(this.cancelUrl)
          .then(response => {
            this.message = response.message
            if (response.resultCode === 'success') {
              this.$toast.info(this.$t(response.message, response.params))
              this.status = 'canceled'
              this.message = this.$t(response.message, response.params)
              this.hideProgressModal()
              this.$emit('on-canceled')
            } else {
              this.$emit('on-cancel-failed')
              console.error(this.$t(response.message, response.params))
              this.$toast.error(this.$t(response.message, response.params))
              this.message = this.$t(response.message, response.params)
            }
          })
          .catch(error => {
            this.$emit('on-cancel-failed')
            if (error.response && error.response.data) {
              this.message = error.response.data.message
            } else {
              this.message = error
            }
          })
      }
    },
    startPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = undefined
      }
      this.pollTimer = setInterval(this.poll, 1000)
    },
    poll() {
      let url = this.pollingUrl
      if (url.indexOf('?') >= 0) {
        url += '&token='
      } else {
        url += '?token='
      }
      url += this.uploadPollingToken
      this.$axios
        .$get(url)
        .then(response => {
          this.message = response.message
          if (
            response.resultCode === 'success' ||
            response.resultCode === 'in_progress'
          ) {
            this.processRate = response.data.processRate
            this.processCo = response.data.processCo
            this.allCo = response.data.allCo
            if (response.resultCode === 'success') {
              this.status = 'success'
            } else {
              this.status = 'processing'
            }
            this.message = response.data.message
            if (response.resultCode === 'success') {
              this.hideProgressModal()
              if (this.pollTimer) {
                clearInterval(this.pollTimer)
                this.pollTimer = undefined
              }
              this.$emit('on-uploaded')
            }
          } else {
            this.$emit('on-failed')
            this.hideProgressModal()
            if (this.pollTimer) {
              clearInterval(this.pollTimer)
              this.pollTimer = undefined
            }
            console.error(this.$t(response.message, response.params))
            this.$toast.error(this.$t(response.message, response.params))
          }
        })
        .catch(error => {
          this.hideProgressModal()
          this.$emit('on-failed')
          if (this.pollTimer) {
            clearInterval(this.pollTimer)
            this.pollTimer = undefined
          }
          this.status = 'fail'
          if (error.response && error.response.data) {
            this.message = error.response.data.message
          } else {
            this.message = error
          }
        })
    }
  }
}
</script>

<style scoped>
.reset-file-button {
  height: 35px;
}
.status-area {
  text-align: right;
  overflow: hidden;
  word-break: break-all;
}
.status-area.status-area-in-modal {
  text-align: center;
}
.cancel-button-area {
  margin-top: 15px;
  text-align: center;
}
</style>
